import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Typography } from '@kizen/kds/Typography';
import { gutters } from 'app/spacing';
import BackButton from 'components/Button/BackButton';
import LoadingButton from 'components/Button/LoadingButton';
import { maybeTrackBuilderSave } from 'utility/analytics';

const SAVE = 'save';
const SAVE_AND_CLOSE = 'save-and-close';

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
`;

const LeftColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const CenterColumn = styled(LeftColumn)`
  justify-content: center;

  > * {
    min-width: 0;
  }
`;

const RightColumn = styled(LeftColumn)`
  justify-content: flex-end;
`;

const StyledBackButton = styled(BackButton)`
  margin-right: ${gutters.spacing()}px;
`;

const SaveButton = styled(LoadingButton)`
  ${({ minWidth }) =>
    minWidth &&
    css`
      min-width: ${minWidth}px;
    `}
`;

export const PageBuilderHeader = ({
  saving = false,
  leftColumnContent = null,
  rightActionButton = null,
  title,
  onSave,
  onSaveAndClose,
  onGoBack,
}) => {
  const { t } = useTranslation();
  const [saveType, setSaveType] = useState(null);

  const handleSave = useCallback(async () => {
    setSaveType(SAVE);
    maybeTrackBuilderSave('library_templates');
    await onSave?.();
  }, [onSave]);

  const hanldeSaveAndClose = useCallback(() => {
    setSaveType(SAVE_AND_CLOSE);
    maybeTrackBuilderSave('library_templates');
    onSaveAndClose?.();
  }, [onSaveAndClose]);

  return (
    <Container>
      <LeftColumn>
        <StyledBackButton variant="text" color="blue" onClick={onGoBack}>
          {t('back')}
        </StyledBackButton>
        {leftColumnContent}
      </LeftColumn>
      <CenterColumn>
        <Typography truncate modalCompatibility size="2xl" weight="light">
          {title}
        </Typography>
      </CenterColumn>
      <RightColumn>
        <SaveButton
          disabled={saving}
          loading={saving && saveType === SAVE}
          minWidth={80}
          onClick={handleSave}
        >
          {t('Save')}
        </SaveButton>
        <SaveButton
          disabled={saving}
          loading={saving && saveType === SAVE_AND_CLOSE}
          minWidth={122}
          onClick={hanldeSaveAndClose}
        >
          {t('Save & Close')}
        </SaveButton>
        {rightActionButton}
      </RightColumn>
    </Container>
  );
};
